import { useMemo, useRef, useState } from 'react';
import { useVoices } from '../api/queries';
import { fetchBlobUrl } from '../api/client';
import type { Voice } from '../api/types';
import { useT } from '../i18n';
import { usePopoverClose } from '../lib/hooks';


/** One voice sample at a time across the app (module state, outside React render). */
const previewState: { audio: HTMLAudioElement | null } = { audio: null };
function stopPreview() {
  previewState.audio?.pause();
  previewState.audio = null;
}
async function playPreview(path: string, onEnd: () => void): Promise<boolean> {
  try {
    const url = await fetchBlobUrl(path);
    const a = new Audio(url);
    previewState.audio = a;
    a.onended = () => {
      URL.revokeObjectURL(url);
      onEnd();
    };
    await a.play();
    return true;
  } catch {
    return false;
  }
}

function voiceMeta(v: Voice): string {
  const parts: string[] = [];
  if (typeof v.language === 'string' && v.language) parts.push(v.language.toUpperCase());
  if (typeof v.gender === 'string' && v.gender) parts.push(v.gender);
  if (typeof v.mode === 'string' && v.mode) parts.push(v.mode);
  if (typeof v.description === 'string' && v.description) parts.push(v.description);
  return parts.join(' · ');
}

/**
 * Searchable voice picker (replaces a native <select>, which is unusable with ~50 hash-named voices):
 * voices of the current language first, others below, and a ▶ preview for each voice.
 * `value === ''` means "server default for this language".
 */
export function VoicePicker({ lang, value, onChange, disabled }: { lang: string; value: string; onChange: (voice: string) => void; disabled?: boolean }) {
  const t = useT();
  const voices = useVoices();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [playing, setPlaying] = useState<string | null>(null);
  const ref = usePopoverClose(open, () => setOpen(false));
  const searchRef = useRef<HTMLInputElement>(null);

  const defaultName = voices.data?.defaults?.[lang];
  const { mine, others } = useMemo(() => {
    const all = voices.data?.voices ?? [];
    const needle = q.trim().toLowerCase();
    const match = (v: Voice) => !needle || v.name.toLowerCase().includes(needle) || voiceMeta(v).toLowerCase().includes(needle);
    const sameLang = (v: Voice) => !v.language || v.language === lang || v.language.startsWith(lang);
    return { mine: all.filter((v) => sameLang(v) && match(v)), others: all.filter((v) => !sameLang(v) && match(v)) };
  }, [voices.data, q, lang]);

  const preview = async (name: string) => {
    stopPreview();
    if (playing === name) {
      setPlaying(null);
      return;
    }
    setPlaying(name);
    const ok = await playPreview(`/api/speech/preview?lang=${encodeURIComponent(lang)}&voice=${encodeURIComponent(name)}`, () =>
      setPlaying((p) => (p === name ? null : p)),
    );
    if (!ok) setPlaying(null);
  };

  const pick = (name: string) => {
    onChange(name);
    setOpen(false);
    setQ('');
  };

  const row = (name: string, label: string, meta: string, previewName: string | null) => (
    <div key={name || '__default'} className={`voice-row${value === name ? ' sel' : ''}`}>
      <button type="button" className="voice-pick" onClick={() => pick(name)} aria-pressed={value === name}>
        <span className="voice-name">{label}</span>
        {meta && <span className="voice-meta">{meta}</span>}
      </button>
      {previewName && (
        <button type="button" className="voice-play" onClick={() => void preview(previewName)} aria-label={t('speech.previewVoice', { name: previewName })}>
          {playing === previewName ? '■' : '▶'}
        </button>
      )}
    </div>
  );

  const current = value || (defaultName ? t('speech.defaultVoiceNamed', { name: defaultName }) : t('speech.defaultVoice'));
  const currentVoice = (voices.data?.voices ?? []).find((v) => v.name === (value || defaultName));
  const currentLang = typeof currentVoice?.language === 'string' ? currentVoice.language.toUpperCase() : null;
  return (
    <div className="pop-anchor grow">
      <button
        type="button"
        className="select sm grow voice-trigger"
        aria-label={t('speech.voice')}
        aria-expanded={open}
        disabled={disabled}
        title={voices.isError ? t('speech.voicesUnavailable') : undefined}
        onClick={() => {
          setOpen(!open);
          setTimeout(() => searchRef.current?.focus(), 0);
        }}
      >
        <span className="voice-trigger-name">{current}</span>
        {currentLang && <span className="tag muted voice-lang">{currentLang}</span>}
      </button>
      {open && (
        <div ref={ref} className="popover voice-popover" style={{ left: 0, top: 36 }}>
          <input ref={searchRef} className="input sm" type="search" placeholder={t('speech.searchVoices')} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t('speech.searchVoices')} />
          <div className="voice-list">
            {!q && row('', t('speech.defaultVoiceNamed', { name: defaultName ?? '—' }), t('speech.defaultHint'), defaultName ?? null)}
            {mine.map((v) => row(v.name, v.name, voiceMeta(v), v.name))}
            {others.length > 0 && <div className="voice-group">{t('speech.otherLanguages')}</div>}
            {others.map((v) => row(v.name, v.name, voiceMeta(v), v.name))}
            {mine.length + others.length === 0 && <div className="muted small" style={{ padding: 8 }}>{t('speech.noVoices')}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
